// ImportacionesListXL - Sprint 44 Fase 3 Vista 10.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import apiClient from '../../api/apiClient';
import useImportacionesListXL from './hooks/useImportacionesListXL';
import XLTable from '../../components/xl/XLTable';
import XLInput from '../../components/xl/XLInput';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import { TIPOS, TIPO_VARIANT, fmtNum } from './utils';

const CSS_ID = 'xl-imp-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid var(--xl-color-border);
  background: transparent; color: var(--xl-color-text-secondary); cursor: pointer;
}
.xl-btn:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse); border-color: transparent }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn-row { display: flex; gap: 8px; align-items: center }

.xl-filters {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
  display: flex; flex-direction: column; gap: 12px;
}
.xl-pills { display: flex; flex-wrap: wrap; gap: 6px }
.xl-pill-icon { display: inline-block; margin-right: 4px; line-height: 1 }
.xl-banner {
  padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px;
}
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-pager {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 4px; margin-top: 12px;
  color: var(--xl-color-text-secondary); font-size: 13px;
}
.xl-pager__group { display: flex; gap: 8px; align-items: center }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-tracking-cell {
  display: flex; flex-direction: column; gap: 2px; line-height: 1.3;
}
.xl-tracking-cell__name { font-size: 11px; color: var(--xl-color-text); font-weight: 500 }
.xl-tracking-cell__num { font-family: var(--xl-font-mono); font-size: 11px; color: var(--xl-color-text-muted) }
.xl-row-actions {
  display: flex; gap: 4px; justify-content: flex-end;
}
`;

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

const TIPO_FILTROS = [
  { value: '', label: 'Todos' },
  ...TIPOS.map((t) => ({ value: t.k, label: t.label, icon: t.icon })),
];

export default function ImportacionesListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const navigate = useNavigate();
  const ix = useImportacionesListXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(ix.setQ, 300);

  const onPdf = (imp) => {
    const base = apiClient.API_BASE || '/api';
    window.open(base + '/importaciones/' + imp.id + '/pdf', '_blank');
  };

  const askAndDelete = (imp) => {
    if (!window.confirm('Eliminar ' + imp.referencia + '?')) return;
    ix.remove(imp.id);
  };

  const tipoLabelMap = useMemo(() => {
    const m = {};
    TIPOS.forEach((t) => { m[t.k] = t.label; });
    return m;
  }, []);

  const columns = useMemo(() => ([
    { key: 'referencia', label: 'Referencia',
      render: (i) => <span className="xl-mono">{i.referencia}</span> },
    { key: 'tipo_importacion', label: 'Tipo',
      render: (i) => (
        <XLBadge variant={TIPO_VARIANT[i.tipo_importacion] || 'neutral'}>
          {tipoLabelMap[i.tipo_importacion] || i.tipo_importacion}
        </XLBadge>
      ) },
    { key: 'proveedor', label: 'Proveedor',
      render: (i) => i.proveedor || '\u2014' },
    { key: 'fecha', label: 'Fecha',
      render: (i) => i.fecha || ((i.fecha_creacion || '').slice(0, 10)) || '\u2014' },
    { key: 'total_usd', label: 'Total USD', align: 'right',
      render: (i) => <span className="xl-mono">{fmtNum(i.total_usd)}</span> },
    { key: 'total_cop', label: 'Total COP', align: 'right',
      render: (i) => <span className="xl-mono">{fmtNum(i.total_cop)}</span> },
    { key: 'estado', label: 'Estado',
      render: (i) => <XLBadge variant="neutral">{i.estado}</XLBadge> },
    { key: 'tracking', label: 'Tracking',
      render: (i) => {
        if (i.courier_tracking) {
          return (
            <div className="xl-tracking-cell">
              <span className="xl-tracking-cell__name">
                {(i.courier && i.courier.nombre) || 'Courier'}
              </span>
              <span className="xl-tracking-cell__num">{i.courier_tracking}</span>
            </div>
          );
        }
        if (i.guia_aerea) {
          return (
            <div className="xl-tracking-cell">
              <span className="xl-tracking-cell__name">Guia aerea</span>
              <span className="xl-tracking-cell__num">{i.guia_aerea}</span>
            </div>
          );
        }
        return <span style={{ color: 'var(--xl-color-text-muted)' }}>\u2014</span>;
      } },
    { key: '_acc', label: '', align: 'right',
      render: (i) => (
        <span className="xl-row-actions" onClick={(e) => e.stopPropagation()}>
          <XLChip onClick={() => navigate('/importaciones/' + i.id)}>Editar</XLChip>
          <XLChip onClick={() => onPdf(i)}>PDF</XLChip>
          <XLChip variant="danger" onClick={() => askAndDelete(i)}>Eliminar</XLChip>
        </span>
      ) },
    // askAndDelete captura ix con métodos estables; añadirlo a deps invalidaría
    // useMemo en cada render y haría inútil el cache de columnas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [navigate, tipoLabelMap]);

  const { page, totalPages, total, canPrev, canNext, loading } = ix;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Importaciones</h2>
          <p className="xl-page__sub">Multi-modalidad con tracking universal.</p>
        </div>
        <div className="xl-btn-row">
          <button type="button" className="xl-btn"
            onClick={() => navigate('/couriers')}>Couriers</button>
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={() => navigate('/importaciones/nueva')}>+ Nueva importacion</button>
        </div>
      </header>

      <section className="xl-filters" aria-label="Filtros importaciones">
        <div className="xl-pills" role="tablist">
          {TIPO_FILTROS.map((f) => (
            <XLChip key={f.value || 'all'}
              onClick={() => ix.setTipo(f.value)}
              selected={ix.tipo === f.value}>
              {f.icon && <span className="xl-pill-icon" aria-hidden="true">{f.icon}</span>}
              {f.label}
            </XLChip>
          ))}
        </div>
        <XLInput type="search" label="Buscar"
          placeholder="Referencia, proveedor o descripcion..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)} />
      </section>

      {ix.msg && (
        <div className={'xl-banner ' + (ix.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={ix.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={ix.clearMsg}>
          {ix.msg.text}
        </div>
      )}
      {ix.err && <div className="xl-banner xl-banner--err" role="alert">{ix.err}</div>}

      <XLTable ariaLabel="Lista de importaciones"
        columns={columns} data={ix.items} loading={loading}
        onRowClick={(i) => navigate('/importaciones/' + i.id)}
        rowKey="id" emptyMessage="Sin importaciones para estos filtros." />

      <div className="xl-pager">
        <span>{total
          ? ('Pagina ' + page + ' de ' + totalPages + ' \u00b7 ' + total + ' resultados')
          : 'Sin resultados'}</span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn"
            disabled={!canPrev || loading}
            onClick={() => ix.setPage(page - 1)}>Anterior</button>
          <span className="xl-mono">{page} / {totalPages}</span>
          <button type="button" className="xl-btn"
            disabled={!canNext || loading}
            onClick={() => ix.setPage(page + 1)}>Siguiente</button>
        </span>
      </div>
    </div>
  );
}
